import { isTauri } from '@heidi/runtime/platform';
import {
  getCurrentUser,
  invalidateCurrentUserComposite,
  updateUser,
} from '@heidi/workspace/data';
import type {
  DictateBridgeOnboardingData as DictateOnboardingData,
  DictateBridgeOnboardingHotkeyData as DictateOnboardingHotkeyData,
} from '../../../contracts/dictation-bridge.types';
import { getDictationBridgePort } from '../../../dictation-bridge-ports';
import { getDictationHotkeyPorts } from '../../onboarding/hotkey/hotkey-ports';
import { type DictationHotkeyFormValues } from '../../onboarding/hotkey/useAuthenticatedDictateOnboardingHotkeyForm';
import {
  DICTATION_HOTKEY_DEFAULTS,
  encodeChord,
  getUserPlatform,
  PLATFORM_HOTKEYS_FIELD,
} from '../../onboarding/hotkey/utils';
import { decodeChord } from '../../utils/platformKeyNames';
import { type DictateStep } from '../dictate-onboarding.wizard';
import { VISIBLE_STEPS } from '../step-registry';

export type DictateOnboardingPersistedState = {
  currentStep: DictateStep;
  hotkey: DictateOnboardingHotkeyData | null;
  /** Durable flag set once the user finishes (or exits) onboarding. */
  hasCompletedOnboarding: boolean;
};

const VISIBLE_STEP_SET = new Set<string>(VISIBLE_STEPS);

function isDictateStep(value: string): value is DictateStep {
  return VISIBLE_STEP_SET.has(value);
}

function hotkeyDataToChord(hotkey: DictateOnboardingHotkeyData): string[] {
  return encodeChord({ modifiers: hotkey.modifiers, key: hotkey.key });
}

function chordToHotkeyData(chord: string[]): DictateOnboardingHotkeyData {
  const platform = getUserPlatform();
  const { modifiers, key } = decodeChord(platform, chord);
  return { modifiers, key };
}

function toPersistedState(
  data: DictateOnboardingData
): Partial<DictateOnboardingPersistedState> {
  const restored: Partial<DictateOnboardingPersistedState> = {};

  if (data.current_step && isDictateStep(data.current_step)) {
    restored.currentStep = data.current_step;
  }

  if (data.hotkey) {
    restored.hotkey = data.hotkey;
  }

  if (data.has_completed_dictation_onboarding) {
    restored.hasCompletedOnboarding = true;
  }

  return restored;
}

function toDiskPayload(
  state: Partial<DictateOnboardingPersistedState>
): DictateOnboardingData {
  return {
    current_step: state.currentStep ?? null,
    hotkey: state.hotkey ?? null,
    // Omit unless set so flow-step patches don't write a `false`/`null` flag.
    ...(state.hasCompletedOnboarding ? { has_completed_dictation_onboarding: true } : {}),
  };
}

/** Load persisted onboarding state from disk. */
export async function loadPersistedOnboardingState(): Promise<Partial<DictateOnboardingPersistedState> | null> {
  if (!isTauri()) return null;

  const data = await getDictationBridgePort().loadDictateOnboardingData();
  if (!data) return null;

  const restored = toPersistedState(data);
  return Object.keys(restored).length > 0 ? restored : null;
}

/** Merge a partial update into the persisted onboarding document. */
export async function patchPersistedOnboardingState(
  patch: Partial<DictateOnboardingPersistedState>
): Promise<void> {
  if (!isTauri()) return;

  const existing = await getDictationBridgePort().loadDictateOnboardingData();
  const merged = {
    ...toPersistedState(existing ?? {}),
    ...patch,
  };

  await getDictationBridgePort().saveDictateOnboardingData(toDiskPayload(merged));
}

/** Remove all persisted onboarding data. */
export async function clearPersistedOnboardingState(): Promise<void> {
  if (!isTauri()) return;
  await getDictationBridgePort().clearDictateOnboardingData();
}

/**
 * Persist the durable `has_completed_dictation_onboarding` flag once the flow
 * ends. Runs after {@link clearPersistedOnboardingState}, so the merged document
 * holds only the flag (transient resume fields already gone).
 */
export async function markDictateOnboardingComplete(): Promise<void> {
  await patchPersistedOnboardingState({ hasCompletedOnboarding: true });
}

/** Whether the user has previously finished the dictate onboarding flow. */
export async function hasCompletedDictationOnboarding(): Promise<boolean> {
  if (!isTauri()) return false;
  const restored = await loadPersistedOnboardingState();
  return Boolean(restored?.hasCompletedOnboarding);
}

/** Persist hotkey selection to onboarding_data and apply it to the native listener. */
export async function persistOnboardingHotkeySelection(
  data: DictationHotkeyFormValues
): Promise<string[]> {
  const hotkey: DictateOnboardingHotkeyData = {
    modifiers: data.modifiers,
    key: data.key,
  };
  const chord = hotkeyDataToChord(hotkey);

  if (isTauri()) {
    await patchPersistedOnboardingState({ hotkey });
    await getDictationHotkeyPorts().setDictationHotkey(data.modifiers, data.key);
  }

  return chord;
}

/** Clear a custom hotkey from onboarding_data (user chose platform default). */
export async function clearOnboardingHotkeySelection(): Promise<void> {
  await patchPersistedOnboardingState({ hotkey: null });
}

/** Reset onboarding hotkey to the platform default on disk and in the native listener. */
export async function resetOnboardingHotkeyToPlatformDefault(): Promise<void> {
  const platform = getUserPlatform();
  const defaultChord = [...DICTATION_HOTKEY_DEFAULTS[platform]];
  const { modifiers, key } = decodeChord(platform, defaultChord);

  await clearOnboardingHotkeySelection();
  if (isTauri()) {
    await getDictationHotkeyPorts().setDictationHotkey(modifiers, key);
  }
}

/** Read the hotkey chord from onboarding_data, if a custom key was chosen. */
export async function readOnboardingHotkeySelection(): Promise<string[] | null> {
  const restored = await loadPersistedOnboardingState();
  if (!restored?.hotkey) return null;
  return hotkeyDataToChord(restored.hotkey);
}

export type SyncPendingDictationHotkeyResult = 'synced' | 'cleared' | 'skipped';

/**
 * Sync a disk-persisted hotkey to the user profile once authenticated, then
 * clear all pre-auth onboarding data from disk.
 *
 * Returns `'skipped'` when prerequisites are not met (no user, no disk data)
 * so callers can retry later. Disk is only cleared after a successful write.
 */
export async function syncPendingDictationHotkeyToUser(): Promise<SyncPendingDictationHotkeyResult> {
  if (!isTauri() || !getCurrentUser()) return 'skipped';

  const restored = await loadPersistedOnboardingState();
  if (!restored) return 'skipped';

  if (restored.hotkey) {
    const chord = hotkeyDataToChord(restored.hotkey);
    const platform = getUserPlatform();

    await updateUser({
      dictation: {
        [PLATFORM_HOTKEYS_FIELD[platform]]: chord,
      },
    });
    await invalidateCurrentUserComposite();
  }

  await clearPersistedOnboardingState();
  return restored.hotkey ? 'synced' : 'cleared';
}

/** Convert an in-memory selected chord to the persisted hotkey shape. */
export function selectedHotkeyToHotkeyData(
  chord: string[] | null
): DictateOnboardingHotkeyData | null {
  if (!chord) return null;
  return chordToHotkeyData(chord);
}

/** Convert persisted hotkey data to the in-memory selected chord. */
export function hotkeyDataToSelectedChord(hotkey: DictateOnboardingHotkeyData): string[] {
  return hotkeyDataToChord(hotkey);
}
